/* eslint-disable no-param-reassign */

import produce, {castDraft} from 'immer';

import {ExpressionAction, ExpressionActionTypesEnum} from './expressionActions';
import {ClientAtom, isFunctionAtom} from './expressionAtomModel';
import {buildExpression, inferAtom} from './expressionInferHelpers';
import {Expression, ExpressionId} from './expressionModel';
import {
  addAtomToFunction,
  allocateExpressionId,
  getAtomPosition,
  getExpressionWithId,
  inferAndValidateExpressions,
} from './expressionReducerHelpers';
import {createExpressionsContext} from './expressionsContext';
import {ExpressionDependencies, ExpressionState, ExpressionValidator} from './expressionState';

/*
 * Reducer.
 */

export function expressionReducer<S extends ExpressionState, A extends ExpressionAction<S>>(
  state: S,
  action: A,
  dependencies: ExpressionDependencies = {},
): S {
  switch (action.type) {
    case ExpressionActionTypesEnum.ADD_EXPRESSION:
      return addExpressionReducer(state, action.atoms, dependencies);

    case ExpressionActionTypesEnum.UPDATE_EXPRESSION_NAME:
      return updateExpressionNameReducer(state, action.expressionId, action.name);

    case ExpressionActionTypesEnum.DELETE_EXPRESSION:
      return deleteExpressionReducer(state, action.expressionId, dependencies, action.validator);

    case ExpressionActionTypesEnum.ADD_ATOM:
      return addAtomReducer(
        state,
        action.expressionId,
        action.atom,
        action.functionAtomClientId,
        dependencies,
        action.validator,
      );

    case ExpressionActionTypesEnum.UPDATE_ATOM:
      return updateAtomReducer(
        state,
        action.expressionId,
        action.atomClientId,
        action.update,
        dependencies,
        action.validator,
      );

    case ExpressionActionTypesEnum.UPDATE_DRY_RUN:
      return updateDryRunReducer(state, action.expressions, action.selectedExpression);

    case ExpressionActionTypesEnum.CLEAR_DRY_RUN:
      return clearDryRunReducer(state);

    case ExpressionActionTypesEnum.DRY_RUN_ERROR:
      return dryRunErrorReducer(state, action.errorMessage);

    case ExpressionActionTypesEnum.DELETE_ATOM:
      return deleteAtomReducer(
        state,
        action.expressionId,
        action.atomClientId,
        dependencies,
        action.validator,
      );

    case ExpressionActionTypesEnum.SET_PANEL_OPEN:
      return setPanelOpenReducer(state, action.isOpen);

    case ExpressionActionTypesEnum.SAVE_DRAFT_EXPRESSION:
      return saveDraftExpressionReducer(state, dependencies, action.validator);

    case ExpressionActionTypesEnum.SELECT_EXPRESSION:
      return selectExpressionReducer(state, action.id);

    default:
      return state;
  }
}

/*
 * Helpers.
 */

export function addExpressionReducer<S extends ExpressionState>(
  state: S,
  atoms: ReadonlyArray<ClientAtom>,
  dependencies: ExpressionDependencies,
) {
  return produce(state, (draft) => {
    const {expressions} = draft;
    const context = createExpressionsContext({
      expressions,
      customFields: state.customFields,
      initialInfer: state.initialInfer,
    });

    const id = allocateExpressionId(draft);
    const expression = buildExpression(id, '', atoms, context, dependencies);

    draft.expressionPanelState.selectedExpression = castDraft(expression);

    inferAndValidateExpressions(draft, dependencies);
  });
}

function updateExpressionNameReducer<S extends ExpressionState>(
  state: S,
  expressionId: ExpressionId,
  name: string,
) {
  return produce(state, (draft) => {
    const expression = getExpressionWithId(draft, expressionId);

    if (expression) {
      expression.name = name;
    }
  });
}

function deleteExpressionReducer<S extends ExpressionState>(
  state: S,
  expressionId: ExpressionId,
  dependencies: ExpressionDependencies,
  validator?: ExpressionValidator<S>,
) {
  return produce(state, (draft) => {
    const {expressions} = draft;
    const index = expressions.findIndex((expression) => expression.id === expressionId);

    if (index >= 0) {
      expressions.splice(index, 1);
    }

    draft.expressionPanelState.selectedExpression = null;
    draft.expressionPanelState.isOpen = false;

    inferAndValidateExpressions(draft, dependencies, validator);
  });
}

function addAtomReducer<S extends ExpressionState>(
  state: S,
  expressionId: ExpressionId,
  atom: ClientAtom,
  functionAtomClientId: string | undefined,
  dependencies: ExpressionDependencies,
  validator?: ExpressionValidator<S>,
) {
  return produce(state, (draft) => {
    const {expressions} = draft;
    const expression = getExpressionWithId(draft, expressionId);

    if (!expression) {
      return;
    }

    const {atoms} = expression;
    const context = createExpressionsContext({
      expressions,
      customFields: state.customFields,
      initialInfer: state.initialInfer,
    });

    // Atom can be added to the expression directly or to a function atom.
    const functionAtom = functionAtomClientId && atoms.find((a) => a.clientId === functionAtomClientId);

    if (functionAtom && isFunctionAtom(functionAtom)) {
      addAtomToFunction(functionAtom, atom, context, dependencies);
    } else {
      atoms.push(castDraft(atom));
    }

    inferAndValidateExpressions(draft, dependencies, validator);
  });
}

function updateAtomReducer<S extends ExpressionState>(
  state: S,
  expressionId: ExpressionId,
  atomClientId: string,
  update: (atom: ClientAtom) => ClientAtom,
  dependencies: ExpressionDependencies,
  validator?: ExpressionValidator<S>,
) {
  return produce(state, (draft) => {
    const {expressions, customFields, initialInfer} = draft;
    const expression = getExpressionWithId(draft, expressionId);

    if (!expression) {
      return;
    }

    const position = getAtomPosition(expression.atoms, atomClientId);
    if (!position) {
      return;
    }

    const context = createExpressionsContext({expressions, customFields, initialInfer});
    const [index, atoms] = position;
    const originalAtom = atoms[index];
    const updatedAtom = update(originalAtom);
    updatedAtom.inferredType = inferAtom(updatedAtom, context, dependencies);
    atoms[index] = castDraft(updatedAtom);

    inferAndValidateExpressions(draft, dependencies, validator);
  });
}

function deleteAtomReducer<S extends ExpressionState>(
  state: S,
  expressionId: ExpressionId,
  atomClientId: string,
  dependencies: ExpressionDependencies,
  validator?: ExpressionValidator<S>,
) {
  return produce(state, (draft) => {
    const expression = getExpressionWithId(draft, expressionId);

    if (!expression) {
      return;
    }

    const position = getAtomPosition(expression.atoms, atomClientId);
    if (!position) {
      return;
    }

    const [index, atoms] = position;
    atoms.splice(index, 1);

    inferAndValidateExpressions(draft, dependencies, validator);
  });
}

function updateDryRunReducer<S extends ExpressionState>(
  state: S,
  expressions: ReadonlyArray<Expression>,
  selectedExpression: Expression | null,
) {
  return produce(state, (draft) => {
    draft.expressions = castDraft(expressions);
    draft.expressionPanelState.selectedExpression = castDraft(selectedExpression);
    draft.dryRunErrorMessage = undefined;
  });
}

function clearDryRunReducer<S extends ExpressionState>(state: S) {
  return produce(state, (draft) => {
    const {selectedExpression} = draft.expressionPanelState;
    const allExpressions = selectedExpression
      ? [...draft.expressions, selectedExpression]
      : draft.expressions;

    allExpressions.forEach((expression) => {
      expression.dryRun = undefined;

      for (const atom of visitExpression(expression)) {
        atom.dryRun = undefined;
      }
    });

    draft.dryRunErrorMessage = undefined;
  });
}

function dryRunErrorReducer<S extends ExpressionState>(state: S, errorMessage: string) {
  return produce(state, (draft) => {
    draft.dryRunErrorMessage = errorMessage;
  });
}

export function setPanelOpenReducer<S extends ExpressionState>(state: S, isOpen: boolean) {
  return produce(state, (draft) => {
    draft.expressionPanelState.isOpen = isOpen;

    if (!isOpen) {
      draft.expressionPanelState.selectedExpression = null;
    }
  });
}

function saveDraftExpressionReducer<S extends ExpressionState>(
  state: S,
  dependencies: ExpressionDependencies,
  validator?: ExpressionValidator<S>,
) {
  return produce(state, (draft) => {
    const {
      expressionPanelState: {selectedExpression},
    } = draft;
    if (!selectedExpression) {
      return;
    }

    const existingIndex = draft.expressions.findIndex((exp) => exp.id === selectedExpression.id);

    if (existingIndex !== -1) {
      draft.expressions[existingIndex] = selectedExpression;
    } else {
      draft.expressions.push(selectedExpression);

      inferAndValidateExpressions(draft, dependencies, validator);
    }

    // Close the P4 panel on save
    draft.expressionPanelState.selectedExpression = null;
    draft.expressionPanelState.isOpen = false;
  });
}

function selectExpressionReducer<S extends ExpressionState>(state: S, id: ExpressionId) {
  return produce(state, (draft) => {
    const expression = getExpressionWithId(draft, id);

    if (!expression) {
      return;
    }

    draft.expressionPanelState.selectedExpression = expression;
    draft.expressionPanelState.isOpen = true;
  });
}

function* visitExpression(expression: Expression): Iterable<ClientAtom> {
  yield* visitAtoms(expression.atoms);
}

function* visitAtoms(atoms: ReadonlyArray<ClientAtom>): Iterable<ClientAtom> {
  for (const atom of atoms) {
    yield atom;

    if (atom.functionAtoms) {
      yield* visitAtoms(atom.functionAtoms);
    }
  }
}
